import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ERPLayout from '@/components/feature/ERPLayout';
import { mockCompanySettings } from '@/mocks/auth';
import SiteSettingsTab from './components/SiteSettingsTab';
import WhatsAppSettingsTab from './components/WhatsAppSettingsTab';
import { settingsApi, authApi } from '@/services/api';
import MediaPickerModal from './components/MediaPickerModal';

// Company settings shape mirrored from DB company_settings key-value table
interface CompanySettings {
  name: string;
  email: string;
  phone: string;
  gstin: string;
  website: string;
  address: string;
  invoicePrefix: string;
  jobCardPrefix: string;
  branchPrefix: string;
}

type SettingsTab = 'site' | 'company' | 'security' | 'email' | 'integrations' | 'whatsapp' | 'profile' | 'password' | 'notifications';

const TIMEOUT_PRESETS = [
  { label: '5 min',  value: 5,   desc: 'Very short — high security' },
  { label: '15 min', value: 15,  desc: 'Short — recommended for shared PCs' },
  { label: '30 min', value: 30,  desc: 'Standard — default setting' },
  { label: '1 hour', value: 60,  desc: 'Relaxed — for private workstations' },
  { label: '2 hours',value: 120, desc: 'Long — for trusted environments' },
  { label: 'Never',  value: 0,   desc: 'Disabled — not recommended' },
];

const WARNING_PRESETS = [
  { label: '30 sec', value: 0.5 },
  { label: '1 min',  value: 1   },
  { label: '2 min',  value: 2   },
  { label: '5 min',  value: 5   },
];

export default function AdminSettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Support ?tab=xxx deep-linking from old /settings page
  const initialTab = (new URLSearchParams(location.search).get('tab') as SettingsTab) || 'site';
  const [tab, setTab] = useState<SettingsTab>(initialTab);
  const [company, setCompany] = useState<CompanySettings>({ ...mockCompanySettings });
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);

  // Load company profile from DB when switching to company tab
  useEffect(() => {
    if (tab !== 'company') return;
    setCompanyLoading(true);
    settingsApi.getCompanyProfile()
      .then((data: Record<string, string>) => {
        if (data && Object.keys(data).length > 0) {
          setCompany({
            name:           data.company_name    || data.name          || mockCompanySettings.name,
            email:          data.company_email   || data.email         || mockCompanySettings.email,
            phone:          data.company_phone   || data.phone         || mockCompanySettings.phone,
            gstin:          data.company_gstin   || data.gstin         || mockCompanySettings.gstin,
            website:        data.company_website || data.website       || mockCompanySettings.website,
            address:        data.company_address || data.address       || mockCompanySettings.address,
            invoicePrefix:  data.invoice_prefix  || data.invoicePrefix || mockCompanySettings.invoicePrefix,
            jobCardPrefix:  data.job_card_prefix || data.jobCardPrefix || mockCompanySettings.jobCardPrefix,
            branchPrefix:   data.branch_prefix   || data.branchPrefix  || mockCompanySettings.branchPrefix || 'HYD',
          });
        }
      })
      .catch(() => { /* keep mock defaults */ })
      .finally(() => setCompanyLoading(false));
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [security, setSecurity] = useState({
    sessionTimeoutMin: 30,
    warningMin: 2,
    maxLoginAttempts: '5',
    passwordMinLength: '8',
    requireUppercase: true,
    requireNumbers: true,
    requireSpecial: false,
    twoFARequired: false,
    ipWhitelist: '',
  });
  const [timeoutSaved, setTimeoutSaved] = useState(false);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);

  // Load session timeout from DB on mount
  useEffect(() => {
    settingsApi.getSessionTimeout()
      .then((data: { timeout_minutes?: number; warning_minutes?: number }) => {
        if (data) {
          setSecurity((prev) => ({
            ...prev,
            sessionTimeoutMin: data.timeout_minutes ?? 30,
            warningMin: data.warning_minutes ?? 2,
          }));
        }
      })
      .catch(() => { /* API offline — keep defaults */ });
  }, []);

  // Load security policy from DB when switching to security tab
  useEffect(() => {
    if (tab !== 'security') return;
    setSecurityLoading(true);
    settingsApi.getSecurityPolicy()
      .then((data: {
        max_login_attempts?: number;
        password_min_length?: number;
        require_uppercase?: boolean;
        require_numbers?: boolean;
        require_special?: boolean;
        two_fa_required?: boolean;
        ip_whitelist?: string;
      }) => {
        if (data) {
          setSecurity((prev) => ({
            ...prev,
            maxLoginAttempts:  String(data.max_login_attempts  ?? prev.maxLoginAttempts),
            passwordMinLength: String(data.password_min_length ?? prev.passwordMinLength),
            requireUppercase:  data.require_uppercase  ?? prev.requireUppercase,
            requireNumbers:    data.require_numbers    ?? prev.requireNumbers,
            requireSpecial:    data.require_special    ?? prev.requireSpecial,
            twoFARequired:     data.two_fa_required    ?? prev.twoFARequired,
            ipWhitelist:       data.ip_whitelist       ?? prev.ipWhitelist,
          }));
        }
      })
      .catch(() => { /* API offline — keep defaults */ })
      .finally(() => setSecurityLoading(false));
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const [email, setEmail] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPass: '',
    fromName: '',
    fromEmail: '',
    smtpEnabled: false,
    smtpSecure: false,
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [smtpStatusMsg, setSmtpStatusMsg] = useState('');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Load SMTP config from DB when switching to email tab
  useEffect(() => {
    if (tab !== 'email') return;
    setEmailLoading(true);
    settingsApi.getSmtpConfig()
      .then((data: Record<string, string>) => {
        if (data) {
          setEmail({
            smtpHost:    data.smtp_host      ?? '',
            smtpPort:    data.smtp_port      ?? '587',
            smtpUser:    data.smtp_user      ?? '',
            smtpPass:    data.smtp_pass      ?? '',   // will be '••••••••' if already set
            fromName:    data.smtp_from_name ?? '',
            fromEmail:   data.smtp_from      ?? '',
            smtpEnabled: data.smtp_enabled   === 'true',
            smtpSecure:  data.smtp_secure    === 'true',
          });
          // Pre-fill test address with from email
          if (data.smtp_from) setTestEmailAddr(data.smtp_from);
        }
      })
      .catch(() => { /* API offline — keep empty defaults */ })
      .finally(() => setEmailLoading(false));
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const personalTabs: { key: SettingsTab; label: string; icon: string }[] = [
    { key: 'profile',       label: 'My Profile',      icon: 'ri-user-3-line' },
    { key: 'password',      label: 'Password',        icon: 'ri-lock-password-line' },
    { key: 'notifications', label: 'Notifications',   icon: 'ri-notification-3-line' },
  ];

  const systemTabs: { key: SettingsTab; label: string; icon: string }[] = [
    { key: 'site',         label: 'Site Settings',   icon: 'ri-global-line' },
    { key: 'company',      label: 'Company Profile', icon: 'ri-building-2-line' },
    { key: 'security',     label: 'Security',        icon: 'ri-shield-keyhole-line' },
    { key: 'email',        label: 'Email / SMTP',    icon: 'ri-mail-settings-line' },
    { key: 'whatsapp',     label: 'WhatsApp',        icon: 'ri-whatsapp-line' },
    { key: 'integrations', label: 'Integrations',    icon: 'ri-plug-line' },
  ];

  // Personal tab content — profile, password, notifications (functional within admin settings)
  const PersonalSettingsPanel = ({ tab: pTab }: { tab: SettingsTab }) => {
    // Profile form state
    const [profile, setProfile] = useState({
      name: '',
      email: '',
      phone: '',
      designation: '',
      department: '',
      bio: '',
      avatar: '',
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [showMediaPicker, setShowMediaPicker] = useState(false);

    // Password form state
    const [passwordData, setPasswordData] = useState({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Notification preferences state
    const [notifPrefs, setNotifPrefs] = useState({
      email_job_assigned: true,
      email_job_completed: true,
      email_invoice_generated: true,
      email_payment_received: true,
      email_site_visit_request: true,
      push_job_assigned: true,
      push_deadline_approaching: true,
      push_payment_received: true,
    });
    const [notifLoading, setNotifLoading] = useState(false);
    const [notifSaving, setNotifSaving] = useState(false);

    // Load profile data
    useEffect(() => {
      if (pTab !== 'profile') return;
      setProfileLoading(true);
      settingsApi.getUserProfile()
        .then((data: Record<string, string>) => {
          setProfile({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            designation: data.designation || '',
            department: data.department || '',
            bio: data.bio || '',
            avatar: data.avatar || '',
          });
        })
        .catch(() => showToast('Failed to load profile', 'error'))
        .finally(() => setProfileLoading(false));
    }, [pTab]);

    // Load notification preferences
    useEffect(() => {
      if (pTab !== 'notifications') return;
      setNotifLoading(true);
      settingsApi.getNotificationPrefs()
        .then((data: Record<string, boolean>) => {
          setNotifPrefs(prev => ({ ...prev, ...data }));
        })
        .catch(() => { /* ignore */ })
        .finally(() => setNotifLoading(false));
    }, [pTab]);

    // Handle avatar selection from media picker
    const handleAvatarSelect = async (url: string) => {
      setShowMediaPicker(false);
      if (!url) return;

      // Save the selected URL to the user's avatar in DB
      try {
        await settingsApi.updateUserProfile({ avatar: url });
      } catch { /* best effort */ }

      setProfile(prev => ({ ...prev, avatar: url }));

      // Update localStorage so ERPLayout sidebar/header reflects the new avatar immediately
      try {
        const stored = localStorage.getItem('erp_user');
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.avatar = url;
          parsed.avatarUrl = url;
          localStorage.setItem('erp_user', JSON.stringify(parsed));
          window.dispatchEvent(new CustomEvent('erp-user-updated', { detail: parsed }));
        }
      } catch { /* ignore */ }

      showToast('Profile photo updated successfully');
    };

    // Save profile
    const handleSaveProfile = async () => {
      setProfileSaving(true);
      try {
        await settingsApi.updateUserProfile({
          name: profile.name,
          email: profile.email,
          phone: profile.phone,
          designation: profile.designation,
          department: profile.department,
          bio: profile.bio,
        });
        showToast('Profile saved successfully');
      } catch (err) {
        showToast('Failed to save profile', 'error');
      } finally {
        setProfileSaving(false);
      }
    };

    // Save password
    const handleSavePassword = async () => {
      setPasswordError('');
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match');
        return;
      }
      if (passwordData.newPassword.length < 8) {
        setPasswordError('Password must be at least 8 characters');
        return;
      }
      setPasswordSaving(true);
      try {
        await authApi.changePassword(passwordData.currentPassword, passwordData.newPassword);
        showToast('Password changed successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } catch (err) {
        setPasswordError('Current password is incorrect');
      } finally {
        setPasswordSaving(false);
      }
    };

    // Save notification preferences
    const handleSaveNotifs = async () => {
      setNotifSaving(true);
      try {
        await settingsApi.updateNotificationPrefs(notifPrefs);
        showToast('Notification preferences saved');
      } catch (err) {
        showToast('Failed to save preferences', 'error');
      } finally {
        setNotifSaving(false);
      }
    };

    // Profile Tab Content
    if (pTab === 'profile') {
      if (profileLoading) {
        return (
          <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
            <div className="flex items-center gap-3 text-gray-400">
              <i className="ri-loader-4-line text-xl animate-spin" style={{ color: '#7c3aed' }} />
              <span className="text-sm">Loading profile...</span>
            </div>
          </div>
        );
      }

      return (
        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#f5f3ff' }}>
              <i className="ri-user-3-line text-base" style={{ color: '#7c3aed' }} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">My Profile</h3>
              <p className="text-xs text-gray-500">Update your personal information and profile photo</p>
            </div>
          </div>

          {/* Avatar — Media Picker */}
          {showMediaPicker && (
            <MediaPickerModal
              onSelect={handleAvatarSelect}
              onClose={() => setShowMediaPicker(false)}
            />
          )}

          <div className="flex items-center gap-5">
            {/* Current avatar preview */}
            <div className="relative flex-shrink-0 group cursor-pointer" onClick={() => setShowMediaPicker(true)}>
              {(() => {
                // Validate avatar URL properly (reject truncated URLs like https://jo/)
                const isValidAvatarUrl = (url: string): boolean => {
                  if (!url || url.length < 10) return false;
                  if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
                  try {
                    const parsed = new URL(url);
                    const hostname = parsed.hostname;
                    if (hostname.length < 4) return false; // Too short to be real
                    if (hostname === 'jo') return false; // Known truncated pattern
                    return true;
                  } catch {
                    return false;
                  }
                };
                const hasValidAvatar = isValidAvatarUrl(profile.avatar);
                return hasValidAvatar ? (
                  <img
                    src={profile.avatar}
                    alt="Avatar"
                    className="w-24 h-24 rounded-full object-cover border-4 border-violet-100 transition-all group-hover:border-violet-300"
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white border-4 border-violet-100 group-hover:border-violet-300 transition-all"
                    style={{ background: '#7c3aed' }}
                  >
                    {profile.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                );
              })()}
              {/* Camera overlay on hover */}
              <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center gap-1">
                  <i className="ri-camera-line text-white text-xl" />
                  <span className="text-white text-xs font-semibold">Change</span>
                </div>
              </div>
            </div>

            {/* Info + actions */}
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-800">Profile Photo</p>
              <p className="text-xs text-gray-400 mt-0.5 mb-3">Pick from your media library or upload a new image</p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setShowMediaPicker(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap border border-violet-200 text-violet-700 hover:bg-violet-50"
                >
                  <i className="ri-gallery-line" />
                  Pick from Media Library
                </button>
                {profile.avatar && (
                  <button
                    onClick={() => handleAvatarSelect('')}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap border border-gray-200 text-gray-500 hover:bg-gray-50"
                  >
                    <i className="ri-delete-bin-line" />
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Full Name</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone</label>
              <input
                type="text"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Designation</label>
              <input
                type="text"
                value={profile.designation}
                onChange={(e) => setProfile({ ...profile, designation: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Department</label>
              <input
                type="text"
                value={profile.department}
                onChange={(e) => setProfile({ ...profile, department: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Bio</label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
              />
            </div>
          </div>

          <button
            onClick={handleSaveProfile}
            disabled={profileSaving}
            className="flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
            style={{ background: '#7c3aed' }}
          >
            {profileSaving ? <><i className="ri-loader-4-line animate-spin" />Saving...</> : <><i className="ri-save-line" />Save Profile</>}
          </button>
        </div>
      );
    }

    // Password Tab Content
    if (pTab === 'password') {
      return (
        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#f5f3ff' }}>
              <i className="ri-lock-password-line text-base" style={{ color: '#7c3aed' }} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Change Password</h3>
              <p className="text-xs text-gray-500">Update your login password</p>
            </div>
          </div>

          {passwordError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
              <i className="ri-error-warning-line" />
              {passwordError}
            </div>
          )}

          <div className="space-y-4 max-w-md">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Current Password</label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">New Password</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              />
            </div>
          </div>

          <button
            onClick={handleSavePassword}
            disabled={passwordSaving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            className="flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
            style={{ background: '#7c3aed' }}
          >
            {passwordSaving ? <><i className="ri-loader-4-line animate-spin" />Changing...</> : <><i className="ri-save-line" />Change Password</>}
          </button>
        </div>
      );
    }

    // Notifications Tab Content
    if (pTab === 'notifications') {
      if (notifLoading) {
        return (
          <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
            <div className="flex items-center gap-3 text-gray-400">
              <i className="ri-loader-4-line text-xl animate-spin" style={{ color: '#7c3aed' }} />
              <span className="text-sm">Loading preferences...</span>
            </div>
          </div>
        );
      }

      return (
        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#f5f3ff' }}>
              <i className="ri-notification-3-line text-base" style={{ color: '#7c3aed' }} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Notification Preferences</h3>
              <p className="text-xs text-gray-500">Choose which events you want to be notified about</p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Email Notifications</p>
              <div className="space-y-2">
                {[
                  { key: 'email_job_assigned', label: 'Job assigned to me' },
                  { key: 'email_job_completed', label: 'Job marked as completed' },
                  { key: 'email_invoice_generated', label: 'Invoice generated' },
                  { key: 'email_payment_received', label: 'Payment received' },
                  { key: 'email_site_visit_request', label: 'New site visit request' },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifPrefs[item.key as keyof typeof notifPrefs] as boolean}
                      onChange={(e) => setNotifPrefs({ ...notifPrefs, [item.key]: e.target.checked })}
                      className="w-4 h-4 accent-violet-600 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Push Notifications</p>
              <div className="space-y-2">
                {[
                  { key: 'push_job_assigned', label: 'Job assigned to me' },
                  { key: 'push_deadline_approaching', label: 'Deadline approaching' },
                  { key: 'push_payment_received', label: 'Payment received' },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifPrefs[item.key as keyof typeof notifPrefs] as boolean}
                      onChange={(e) => setNotifPrefs({ ...notifPrefs, [item.key]: e.target.checked })}
                      className="w-4 h-4 accent-violet-600 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={handleSaveNotifs}
            disabled={notifSaving}
            className="flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
            style={{ background: '#7c3aed' }}
          >
            {notifSaving ? <><i className="ri-loader-4-line animate-spin" />Saving...</> : <><i className="ri-save-line" />Save Preferences</>}
          </button>
        </div>
      );
    }

    return null;
  };

  // navigate is used in PersonalSettingsPanel component

  return (
    <ERPLayout title="System Settings" subtitle="Configure site branding, company profile, security, email, and integrations">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 text-white text-sm px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all`}
          style={{ background: toast.type === 'success' ? '#7c3aed' : '#ef4444' }}
        >
          <i className={toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
          {toast.msg}
        </div>
      )}

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Tab sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {/* Personal section */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Personal</p>
              </div>
              {personalTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all cursor-pointer text-left border-b border-gray-50 ${
                    tab === t.key ? 'font-semibold' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  style={tab === t.key ? { background: '#f5f3ff', color: '#7c3aed' } : {}}
                >
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${t.icon} text-base`} />
                  </span>
                  {t.label}
                </button>
              ))}
              {/* System section */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 border-t border-t-gray-200">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">System</p>
              </div>
              {systemTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all cursor-pointer text-left border-b border-gray-50 last:border-0 ${
                    tab === t.key ? 'font-semibold' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  style={tab === t.key ? { background: '#f5f3ff', color: '#7c3aed' } : {}}
                >
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${t.icon} text-base`} style={t.key === 'whatsapp' ? { color: tab === t.key ? '#7c3aed' : '#25D366' } : {}} />
                  </span>
                  {t.label}
                  {t.key === 'site' && (
                    <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: '#ede9fe', color: '#7c3aed' }}>
                      New
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Quick info card */}
            <div className="mt-4 bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                <i className="ri-information-line mr-1" style={{ color: '#7c3aed' }} />
                Unified Settings
              </p>
              <p className="text-xs text-gray-500 leading-relaxed">
                All personal and system settings are managed here. Changes are saved to MariaDB and applied site-wide.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {/* ── Site Settings ─────────────────────────────────────────────── */}
            {(tab === 'profile' || tab === 'password' || tab === 'notifications') && (
              <PersonalSettingsPanel tab={tab} />
            )}

            {tab === 'site' && (
              <SiteSettingsTab
                onSuccess={(msg) => showToast(msg, 'success')}
                onError={(msg) => showToast(msg, 'error')}
              />
            )}

            {/* ── Company Profile ───────────────────────────────────────────── */}
            {tab === 'company' && (
              <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
                <h3 className="font-bold text-gray-900">Company Profile</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Company Name</label>
                    <input
                      type="text"
                      value={company.name}
                      onChange={(e) => setCompany({ ...company, name: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
                    <input
                      type="email"
                      value={company.email}
                      onChange={(e) => setCompany({ ...company, email: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone</label>
                    <input
                      type="text"
                      value={company.phone}
                      onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">GSTIN</label>
                    <input
                      type="text"
                      value={company.gstin}
                      onChange={(e) => setCompany({ ...company, gstin: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Website</label>
                    <input
                      type="text"
                      value={company.website}
                      onChange={(e) => setCompany({ ...company, website: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Address</label>
                    <textarea
                      value={company.address}
                      onChange={(e) => setCompany({ ...company, address: e.target.value })}
                      rows={2}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Invoice Prefix</label>
                    <input
                      type="text"
                      value={company.invoicePrefix}
                      onChange={(e) => setCompany({ ...company, invoicePrefix: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Job Card Prefix</label>
                    <input
                      type="text"
                      value={company.jobCardPrefix}
                      onChange={(e) => setCompany({ ...company, jobCardPrefix: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Branch Prefix</label>
                    <input
                      type="text"
                      value={company.branchPrefix}
                      onChange={(e) => setCompany({ ...company, branchPrefix: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                    />
                  </div>
                </div>
                <button
                  disabled={companySaving}
                  onClick={async () => {
                    setCompanySaving(true);
                    try {
                      await settingsApi.updateCompanyProfile({
                        company_name:    company.name,
                        company_email:   company.email,
                        company_phone:   company.phone,
                        company_gstin:   company.gstin,
                        company_website: company.website,
                        company_address: company.address,
                        invoice_prefix:  company.invoicePrefix,
                        job_card_prefix: company.jobCardPrefix,
                        branch_prefix:   company.branchPrefix || 'HYD',
                        // Also save camelCase versions for backend compatibility
                        invoicePrefix:   company.invoicePrefix,
                        jobCardPrefix:   company.jobCardPrefix,
                        branchPrefix:    company.branchPrefix || 'HYD',
                      });
                      showToast('Company profile saved to database');
                      window.dispatchEvent(new CustomEvent('company-settings-updated', { detail: company }));
                    } catch {
                      showToast('Failed to save — check API connection', 'error');
                    } finally {
                      setCompanySaving(false);
                    }
                  }}
                  className="flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
                  style={{ background: '#7c3aed' }}
                >
                  {companySaving ? <><i className="ri-loader-4-line animate-spin" />Saving...</> : 'Save Changes'}
                </button>
              </div>
            )}

            {/* ── Security ──────────────────────────────────────────────────── */}
            {tab === 'security' && (
              <div className="space-y-5">

                {/* ── Session Timeout Card ─────────────────────────────────── */}
                <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#f5f3ff' }}>
                      <i className="ri-time-line text-base" style={{ color: '#7c3aed' }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">Session Timeout</h3>
                      <p className="text-xs text-gray-500">Auto-logout users after a period of inactivity</p>
                    </div>
                    {timeoutSaved && (
                      <span className="ml-auto text-xs bg-amber-50 text-amber-600 px-2.5 py-1 rounded-full font-semibold flex items-center gap-1">
                        <i className="ri-checkbox-circle-line" />Applied
                      </span>
                    )}
                  </div>

                  {/* Idle timeout presets */}
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wider">Idle Timeout Duration</p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {TIMEOUT_PRESETS.map((p) => {
                        const isSelected = security.sessionTimeoutMin === p.value;
                        return (
                          <button
                            key={p.value}
                            onClick={() => setSecurity({ ...security, sessionTimeoutMin: p.value })}
                            className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-center cursor-pointer transition-all ${
                              isSelected ? 'border-violet-400' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={isSelected ? { background: '#f5f3ff' } : {}}
                          >
                            <span className={`text-sm font-bold ${isSelected ? 'text-violet-700' : 'text-gray-700'}`}>{p.label}</span>
                            <span className="text-xs text-gray-400 leading-tight hidden sm:block">{p.desc.split('—')[0].trim()}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {security.sessionTimeoutMin === 0
                        ? 'Session timeout is disabled. Users will stay logged in indefinitely.'
                        : `Users will be warned after ${security.sessionTimeoutMin - security.warningMin} min of inactivity, then logged out after ${security.sessionTimeoutMin} min total.`}
                    </p>
                  </div>

                  {/* Custom input */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Custom Timeout (minutes)</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          max={480}
                          value={security.sessionTimeoutMin}
                          onChange={(e) => setSecurity({ ...security, sessionTimeoutMin: Math.max(0, parseInt(e.target.value) || 0) })}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                        />
                        <span className="text-xs text-gray-400 whitespace-nowrap">min</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Warning Before Logout</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {WARNING_PRESETS.map((w) => (
                          <button
                            key={w.value}
                            onClick={() => setSecurity({ ...security, warningMin: w.value })}
                            className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                              security.warningMin === w.value
                                ? 'border-violet-400 text-violet-700'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                            style={security.warningMin === w.value ? { background: '#f5f3ff' } : {}}
                          >
                            {w.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Live preview */}
                  <div className="rounded-xl p-4 border" style={{ background: '#fafafa', borderColor: '#e5e7eb' }}>
                    <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
                      <i className="ri-eye-line" style={{ color: '#7c3aed' }} />
                      Timeline Preview
                    </p>
                    {security.sessionTimeoutMin === 0 ? (
                      <div className="flex items-center gap-2 text-sm text-amber-600">
                        <i className="ri-alert-line" />
                        Session timeout is disabled — users stay logged in forever
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="flex items-center gap-0 h-8">
                          {/* Login */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                            <span className="text-xs text-gray-500 mt-1 whitespace-nowrap">Login</span>
                          </div>
                          {/* Active bar */}
                          <div className="flex-1 h-1.5 rounded-full mx-1" style={{ background: '#10b981' }} />
                          {/* Warning point */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className="w-3 h-3 rounded-full bg-amber-400" />
                            <span className="text-xs text-amber-600 mt-1 whitespace-nowrap">{security.sessionTimeoutMin - security.warningMin}m</span>
                          </div>
                          {/* Warning bar */}
                          <div className="w-16 h-1.5 rounded-full mx-1" style={{ background: '#f59e0b' }} />
                          {/* Logout */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="text-xs text-red-500 mt-1 whitespace-nowrap">{security.sessionTimeoutMin}m</span>
                          </div>
                        </div>
                        <div className="flex justify-between mt-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />Active session</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />Warning modal shown ({security.warningMin} min)</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />Auto-logout</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={async () => {
                      try {
                        await settingsApi.saveSessionTimeout(
                          security.sessionTimeoutMin,
                          security.warningMin
                        );
                        // Notify useSessionTimeout hook to re-read from DB
                        window.dispatchEvent(new CustomEvent('erp-session-timeout-updated', {
                          detail: {
                            timeoutMin: security.sessionTimeoutMin,
                            warningMin: security.warningMin,
                          },
                        }));
                        setTimeoutSaved(true);
                        setTimeout(() => setTimeoutSaved(false), 3000);
                        showToast(
                          security.sessionTimeoutMin > 0
                            ? `Session timeout set to ${security.sessionTimeoutMin} min (warning at ${security.warningMin} min)`
                            : 'Session timeout disabled'
                        );
                      } catch {
                        showToast('Failed to save — check API connection', 'error');
                      }
                    }}
                    className="flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                    style={{ background: '#7c3aed' }}
                  >
                    <i className="ri-save-line" />
                    Apply Timeout Settings
                  </button>
                </div>

                {/* ── Other Security Settings ──────────────────────────────── */}
                <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#f5f3ff' }}>
                      <i className="ri-shield-keyhole-line text-base" style={{ color: '#7c3aed' }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">Login & Password Policy</h3>
                      <p className="text-xs text-gray-500">Control login attempts and password requirements</p>
                    </div>
                    {securityLoading && (
                      <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-400">
                        <i className="ri-loader-4-line animate-spin" />
                        Loading from database...
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Max Login Attempts</label>
                      <input
                        type="number"
                        value={security.maxLoginAttempts}
                        onChange={(e) => setSecurity({ ...security, maxLoginAttempts: e.target.value })}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Min Password Length</label>
                      <input
                        type="number"
                        value={security.passwordMinLength}
                        onChange={(e) => setSecurity({ ...security, passwordMinLength: e.target.value })}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">IP Whitelist (comma-separated)</label>
                      <input
                        type="text"
                        value={security.ipWhitelist}
                        onChange={(e) => setSecurity({ ...security, ipWhitelist: e.target.value })}
                        placeholder="192.168.1.0/24, 10.0.0.1"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Password Rules</p>
                    {[
                      { key: 'requireUppercase', label: 'Require uppercase letters' },
                      { key: 'requireNumbers', label: 'Require numbers' },
                      { key: 'requireSpecial', label: 'Require special characters' },
                      { key: 'twoFARequired', label: 'Require 2FA for all users' },
                    ].map((item) => (
                      <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={security[item.key as keyof typeof security] as boolean}
                          onChange={(e) => setSecurity({ ...security, [item.key]: e.target.checked })}
                          className="w-4 h-4 accent-violet-600 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">{item.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-xs text-gray-400 flex items-center gap-1.5">
                      <i className="ri-database-2-line" />
                      Saved to MariaDB — applied on next login attempt
                    </p>
                    <button
                      disabled={securitySaving}
                      onClick={async () => {
                        setSecuritySaving(true);
                        try {
                          await settingsApi.saveSecurityPolicy({
                            max_login_attempts:  parseInt(security.maxLoginAttempts, 10) || 5,
                            password_min_length: parseInt(security.passwordMinLength, 10) || 8,
                            require_uppercase:   security.requireUppercase,
                            require_numbers:     security.requireNumbers,
                            require_special:     security.requireSpecial,
                            two_fa_required:     security.twoFARequired,
                            ip_whitelist:        security.ipWhitelist,
                          });
                          showToast('Security settings saved to database');
                        } catch {
                          showToast('Failed to save — check API connection', 'error');
                        } finally {
                          setSecuritySaving(false);
                        }
                      }}
                      className="flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
                      style={{ background: '#7c3aed' }}
                    >
                      {securitySaving
                        ? <><i className="ri-loader-4-line animate-spin" />Saving...</>
                        : <><i className="ri-save-line" />Save Security Settings</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Email / SMTP ──────────────────────────────────────────────── */}
            {tab === 'email' && (
              <div className="space-y-5">
                {/* Loading skeleton */}
                {emailLoading ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center gap-3 text-gray-400">
                    <i className="ri-loader-4-line text-xl animate-spin" style={{ color: '#7c3aed' }} />
                    <span className="text-sm">Loading SMTP configuration from database...</span>
                  </div>
                ) : (
                  <>
                    {/* ── Enable / Status Banner ──────────────────────────── */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: email.smtpEnabled ? '#ecfdf5' : '#f9fafb' }}>
                          <i className={`ri-mail-settings-line text-base ${email.smtpEnabled ? 'text-amber-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">SMTP Email Sending</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {email.smtpEnabled
                              ? 'Email sending is enabled — site visit confirmations and notifications will be delivered.'
                              : 'Email sending is disabled — configure credentials below and enable to activate.'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setEmail({ ...email, smtpEnabled: !email.smtpEnabled })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${email.smtpEnabled ? 'bg-amber-500' : 'bg-gray-200'}`}
                      >
                        <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${email.smtpEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* ── SMTP Credentials ───────────────────────────────── */}
                    <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f5f3ff' }}>
                          <i className="ri-server-line text-sm" style={{ color: '#7c3aed' }} />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm">SMTP Server Credentials</h3>
                          <p className="text-xs text-gray-500">Stored securely in the database — never in code.</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">SMTP Host</label>
                          <input
                            type="text"
                            value={email.smtpHost}
                            onChange={(e) => setEmail({ ...email, smtpHost: e.target.value })}
                            placeholder="smtp.gmail.com"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">SMTP Port</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={email.smtpPort}
                              onChange={(e) => setEmail({ ...email, smtpPort: e.target.value })}
                              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                            />
                            <div className="flex gap-1.5 items-center flex-shrink-0">
                              {[
                                { label: '587', hint: 'TLS' },
                                { label: '465', hint: 'SSL' },
                                { label: '25',  hint: 'Plain' },
                              ].map((p) => (
                                <button
                                  key={p.label}
                                  onClick={() => {
                                    setEmail({
                                      ...email,
                                      smtpPort: p.label,
                                      smtpSecure: p.label === '465',
                                    });
                                  }}
                                  className={`px-2 py-1 rounded-lg text-xs font-medium border cursor-pointer transition-all whitespace-nowrap ${
                                    email.smtpPort === p.label
                                      ? 'border-violet-400 text-violet-700'
                                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                  }`}
                                  style={email.smtpPort === p.label ? { background: '#f5f3ff' } : {}}
                                >
                                  {p.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Use <strong>587</strong> (STARTTLS) or <strong>465</strong> (SSL). Port 25 is usually blocked.
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">SMTP Username</label>
                          <input
                            type="text"
                            value={email.smtpUser}
                            onChange={(e) => setEmail({ ...email, smtpUser: e.target.value })}
                            placeholder="your@gmail.com"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">
                            SMTP Password / App Password
                          </label>
                          <input
                            type="password"
                            value={email.smtpPass}
                            onChange={(e) => setEmail({ ...email, smtpPass: e.target.value })}
                            placeholder="Leave blank to keep existing"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            For Gmail, use an <strong>App Password</strong> (not your account password).
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">From Name</label>
                          <input
                            type="text"
                            value={email.fromName}
                            onChange={(e) => setEmail({ ...email, fromName: e.target.value })}
                            placeholder="CADSoft ERP"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">From Email</label>
                          <input
                            type="email"
                            value={email.fromEmail}
                            onChange={(e) => setEmail({ ...email, fromEmail: e.target.value })}
                            placeholder="noreply@cadsoft.in"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                          />
                        </div>
                      </div>

                      {/* SSL toggle */}
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={email.smtpSecure}
                          onChange={(e) => setEmail({ ...email, smtpSecure: e.target.checked, smtpPort: e.target.checked ? '465' : '587' })}
                          className="w-4 h-4 accent-violet-600 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">
                          Use SSL (port 465) — uncheck to use STARTTLS (port 587)
                        </span>
                      </label>

                      {/* Provider quick-fill */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Quick Fill — Common Providers</p>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { name: 'Gmail',     host: 'smtp.gmail.com',        port: '587', secure: false },
                            { name: 'Outlook',   host: 'smtp.office365.com',    port: '587', secure: false },
                            { name: 'Yahoo',     host: 'smtp.mail.yahoo.com',   port: '587', secure: false },
                            { name: 'Zoho',      host: 'smtp.zoho.in',          port: '587', secure: false },
                            { name: 'Mailgun',   host: 'smtp.mailgun.org',      port: '587', secure: false },
                            { name: 'SendGrid',  host: 'smtp.sendgrid.net',     port: '587', secure: false },
                          ].map((p) => (
                            <button
                              key={p.name}
                              onClick={() => setEmail({ ...email, smtpHost: p.host, smtpPort: p.port, smtpSecure: p.secure })}
                              className={`px-3 py-1.5 rounded-xl text-xs font-medium border cursor-pointer transition-all whitespace-nowrap ${
                                email.smtpHost === p.host
                                  ? 'border-violet-400 text-violet-700'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                              style={email.smtpHost === p.host ? { background: '#f5f3ff' } : {}}
                            >
                              {p.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Save button */}
                      <div className="flex items-center justify-between pt-1">
                        <p className="text-xs text-gray-400 flex items-center gap-1.5">
                          <i className="ri-database-2-line" />
                          Saved to <code className="bg-gray-100 px-1 rounded">company_settings</code> table in MariaDB
                        </p>
                        <button
                          disabled={emailSaving}
                          onClick={async () => {
                            setEmailSaving(true);
                            try {
                              await settingsApi.saveSmtpConfig({
                                smtp_host:      email.smtpHost,
                                smtp_port:      email.smtpPort,
                                smtp_user:      email.smtpUser,
                                smtp_pass:      email.smtpPass,
                                smtp_from:      email.fromEmail,
                                smtp_from_name: email.fromName,
                                smtp_enabled:   String(email.smtpEnabled),
                                smtp_secure:    String(email.smtpSecure),
                              });
                              showToast('SMTP configuration saved to database');
                            } catch {
                              showToast('Failed to save — check API connection', 'error');
                            } finally {
                              setEmailSaving(false);
                            }
                          }}
                          className="flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
                          style={{ background: '#7c3aed' }}
                        >
                          {emailSaving ? <><i className="ri-loader-4-line animate-spin" />Saving...</> : <><i className="ri-save-line" />Save SMTP Settings</>}
                        </button>
                      </div>
                    </div>

                    {/* ── Test Email ─────────────────────────────────────── */}
                    <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: '#f5f3ff' }}>
                          <i className="ri-send-plane-line text-sm" style={{ color: '#7c3aed' }} />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm">Send Test Email</h3>
                          <p className="text-xs text-gray-500">Verify your SMTP credentials are working correctly.</p>
                        </div>
                      </div>

                      {/* Status result */}
                      {smtpStatus !== 'idle' && (
                        <div
                          className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
                            smtpStatus === 'success'
                              ? 'bg-amber-50 border-amber-200 text-amber-800'
                              : 'bg-red-50 border-red-200 text-red-800'
                          }`}
                        >
                          <i className={`${smtpStatus === 'success' ? 'ri-checkbox-circle-fill text-amber-500' : 'ri-error-warning-fill text-red-500'} mt-0.5 flex-shrink-0`} />
                          <div>
                            <p className="font-semibold">{smtpStatus === 'success' ? 'Test email sent!' : 'Test failed'}</p>
                            <p className="text-xs mt-0.5 leading-relaxed">{smtpStatusMsg}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <label className="text-xs font-semibold text-gray-600 mb-1 block">Send test to</label>
                          <input
                            type="email"
                            value={testEmailAddr}
                            onChange={(e) => setTestEmailAddr(e.target.value)}
                            placeholder="your@email.com"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                          />
                        </div>
                        <button
                          disabled={testSending || !testEmailAddr.trim()}
                          onClick={async () => {
                            setTestSending(true);
                            setSmtpStatus('idle');
                            setSmtpStatusMsg('');
                            try {
                              const result = await settingsApi.testSmtpEmail(testEmailAddr.trim()) as { message?: string };
                              setSmtpStatus('success');
                              setSmtpStatusMsg(result.message || `Email delivered to ${testEmailAddr}`);
                            } catch (err: unknown) {
                              setSmtpStatus('error');
                              const msg = err instanceof Error ? err.message : 'Unknown error';
                              setSmtpStatusMsg(msg);
                            } finally {
                              setTestSending(false);
                            }
                          }}
                          className="flex items-center gap-2 text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: '#7c3aed' }}
                        >
                          {testSending
                            ? <><i className="ri-loader-4-line animate-spin" />Sending...</>
                            : <><i className="ri-send-plane-line" />Send Test</>}
                        </button>
                      </div>

                      {/* Quick help */}
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-xs text-amber-800 space-y-1.5">
                        <p className="font-semibold flex items-center gap-1.5">
                          <i className="ri-lightbulb-line" />
                          Gmail / Google Workspace setup
                        </p>
                        <ol className="list-decimal list-inside space-y-1 text-amber-700 leading-relaxed">
                          <li>Go to Google Account → Security → 2-Step Verification (must be on)</li>
                          <li>Search &quot;App passwords&quot; → create one for &quot;Mail&quot;</li>
                          <li>Paste the 16-character App Password into the SMTP Password field above</li>
                          <li>Use <strong>smtp.gmail.com</strong>, port <strong>587</strong>, your full Gmail address as username</li>
                        </ol>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── WhatsApp ─────────────────────────────────────────────────── */}
            {tab === 'whatsapp' && (
              <WhatsAppSettingsTab onSuccess={(msg) => showToast(msg, 'success')} onError={(msg) => showToast(msg, 'error')} />
            )}

            {/* ── Integrations ──────────────────────────────────────────────── */}
            {tab === 'integrations' && (
              <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
                <h3 className="font-bold text-gray-900">Integrations</h3>
                {[
                  { name: 'Google Maps API', desc: 'Used for GPS field tracking and location display', status: 'Connected', icon: 'ri-map-2-line', color: '#10b981' },
                  { name: 'WhatsApp Business API', desc: 'Send job updates and invoices via WhatsApp', status: 'Not Connected', icon: 'ri-whatsapp-line', color: '#64748b' },
                  { name: 'Tally ERP', desc: 'Sync invoices and payments with Tally', status: 'Not Connected', icon: 'ri-exchange-line', color: '#64748b' },
                  { name: 'AWS S3 Storage', desc: 'Cloud storage for deliverables and photos', status: 'Connected', icon: 'ri-cloud-line', color: '#10b981' },
                  { name: 'Razorpay', desc: 'Online payment gateway for client invoices', status: 'Not Connected', icon: 'ri-bank-card-line', color: '#64748b' },
                  { name: 'Slack Notifications', desc: 'Send system alerts to Slack channels', status: 'Not Connected', icon: 'ri-slack-line', color: '#64748b' },
                ].map((intg) => (
                  <div key={intg.name} className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-all">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-50">
                      <i className={`${intg.icon} text-lg`} style={{ color: intg.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">{intg.name}</p>
                      <p className="text-xs text-gray-500">{intg.desc}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${intg.status === 'Connected' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                        {intg.status}
                      </span>
                      <button
                        onClick={() => showToast(`${intg.name} ${intg.status === 'Connected' ? 'disconnected' : 'connection initiated'}`)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-xl cursor-pointer transition-all whitespace-nowrap ${intg.status === 'Connected' ? 'border border-red-200 text-red-500 hover:bg-red-50' : 'text-white'}`}
                        style={intg.status !== 'Connected' ? { background: '#7c3aed' } : {}}
                      >
                        {intg.status === 'Connected' ? 'Disconnect' : 'Connect'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
